import React from 'react';
import { ListCardBar } from '@/components/Cards/ListCardBar';
import { getUserKind } from '@/components/ui/UserTypeIcon';

interface SectionListViewProps {
    title: string;
    icon: React.ReactNode;
    onViewAll?: () => void;
    items: any[];
    onItemClick: (item: any) => void;
    sectionId: string;
    isSold?: boolean;
    isBrand?: boolean;
    forceAuction?: boolean;
    forceOffer?: boolean;
}

// Same extraction logic as CardRenderer.parseCardLabels so list view matches card info exactly
function parseCardLabels(itemData: any) {
    let rawUsername =
        itemData.verified_username ||
        itemData.card_username ||
        itemData.target_username ||
        itemData.listing_data?.username ||
        itemData.card_name ||
        itemData.displayName ||
        itemData.name ||
        itemData.username ||
        (itemData.serial ? `SERIAL_${itemData.serial}` : undefined) ||
        'CARD';

    if (typeof rawUsername === 'object' && rawUsername?.username) {
        rawUsername = rawUsername.username;
    }
    const targetUsername = String(rawUsername).replace(/^@/, '').toUpperCase();

    const firstName =
        itemData.seller_first_name ||
        itemData.seller?.first_name ||
        itemData.owner?.first_name ||
        itemData.user?.first_name ||
        itemData.seller?.firstName ||
        itemData.owner?.firstName ||
        itemData.first_name;

    const lastName =
        itemData.seller_last_name ||
        itemData.seller?.last_name ||
        itemData.owner?.last_name ||
        itemData.user?.last_name ||
        itemData.seller?.lastName ||
        itemData.owner?.lastName ||
        itemData.last_name ||
        '';

    let personName: string;
    if (firstName && typeof firstName === 'string' && firstName.trim()) {
        personName = `${firstName.trim()} ${lastName ? String(lastName).trim() : ''}`.trim();
    } else {
        const explicitOwner =
            itemData.seller_name ||
            itemData.sellerName ||
            itemData.ownerName ||
            itemData.owner_name ||
            itemData.seller_username ||
            itemData.seller?.username ||
            itemData.owner?.username ||
            (typeof itemData.owner === 'string' ? itemData.owner : undefined) ||
            (typeof itemData.seller === 'string' ? itemData.seller : undefined);

        if (explicitOwner && typeof explicitOwner === 'string' && explicitOwner.trim()) {
            personName = explicitOwner.trim();
        } else {
            personName = targetUsername
                .split('_')
                .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
                .join(' ');
        }
    }

    return { personName: personName.replace(/^@/, ''), targetUsername };
}

export const SectionListView: React.FC<SectionListViewProps> = ({
    title,
    icon,
    onViewAll,
    items = [],
    onItemClick,
    sectionId,
    isSold,
    isBrand,
    forceAuction,
    forceOffer,
}) => {
    return (
        <div>
            {title && (
                <div className="px-4 mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-white/90 flex items-center gap-2">
                        {icon}
                        {title}
                    </h2>
                    {onViewAll && (
                        <button onClick={onViewAll} className="text-sm font-semibold text-blue-400 hover:text-blue-300 active:scale-95 transition-all">
                            View All
                        </button>
                    )}
                </div>
            )}
            <div className="px-4 flex flex-col gap-2.5">
                {items.map((item: any) => {
                    const { personName, targetUsername } = parseCardLabels(item);

                    // Avatar — same chain + normalization as CardRenderer
                    let avatarUrl: string | undefined =
                        item.avatarUrl ||
                        item.ownerAvatar ||
                        item.ownerImage ||
                        item.seller_avatar ||
                        item.seller?.avatar ||
                        item.owner_avatar ||
                        item.profileImage ||
                        item.image ||
                        item.image_filename;
                    if (!avatarUrl || typeof avatarUrl !== 'string' || avatarUrl.includes('placeholder')) {
                        avatarUrl = '/icons/default-avatar.svg';
                    } else if (avatarUrl.startsWith('card_Image/')) {
                        avatarUrl = `/${avatarUrl}`;
                    }

                    // Price — same chain + k-formatting as CardRenderer
                    let priceVal: string | number =
                        item.price ?? item.current_bid ?? item.startPrice ?? item.amount ?? '1,000';
                    if (typeof priceVal === 'number') {
                        priceVal = priceVal >= 1000 ? (priceVal / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : priceVal.toLocaleString();
                    }

                    let rawBg =
                        item.bg ||
                        item.waveColor ||
                        item.wave_color ||
                        item.base_color ||
                        item.color ||
                        item.background_color;

                    const isAuction = Boolean(
                        forceAuction ||
                        item.type === 'AUCTION' ||
                        item.isAuction ||
                        item.isActiveAuction ||
                        item.auction_id ||
                        item.auctionId ||
                        (item.timeLeftSeconds !== undefined && item.timeLeftSeconds > 0)
                    );

                    const isOffer = Boolean(
                        forceOffer ||
                        item.isOffer ||
                        item.listing_type === 'offer' ||
                        item.type === 'offer' ||
                        item.type === 'OFFER' ||
                        item.sale_type === 'offer' ||
                        item.context === 'offer'
                    );

                    return (
                        <ListCardBar
                            key={`${sectionId}-${item.id}`}
                            displayName={personName}
                            username={targetUsername}
                            avatarUrl={avatarUrl}
                            price={priceVal}
                            bg={rawBg || undefined}
                            textColor={item.text_color || item.textColor}
                            sectionId={sectionId}
                            itemType={item.type || 'HOME'}
                            isAuction={isAuction}
                            isOffer={isOffer}
                            isSold={isSold}
                            isBrand={isBrand}
                            userKind={getUserKind(item)}
                            onClick={() => onItemClick(item)}
                        />
                    );
                })}
            </div>
        </div>
    );
};